import React from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { PersonalMovie } from '../services/personalMoviesService';

interface PersonalMovieCardProps {
  movie: PersonalMovie;
  onEdit: (movie: PersonalMovie) => void;
  onDelete: (id: string) => void;
}

const PersonalMovieCard: React.FC<PersonalMovieCardProps> = ({
  movie,
  onEdit,
  onDelete
}) => {
  // Add defensive check for movie object
  if (!movie || !movie.id) {
    console.warn('PersonalMovieCard received invalid movie:', movie);
    return null;
  }
  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Edit clicked for movie:', movie.title);
    onEdit(movie);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Delete clicked for movie:', movie.title);
    if (window.confirm(`Are you sure you want to delete "${movie.title || 'this movie'}"?`)) {
      onDelete(movie.id);
    }
  };

  // Create a gradient background based on the selected color
  const gradientStyle = {
    background: `linear-gradient(135deg, ${movie.color || '#3b82f6'} 0%, ${movie.color || '#3b82f6'}dd 50%, ${movie.color || '#3b82f6'}bb 100%)`,
  };

  return (
    <div className="card group w-full md:w-48 transition-all duration-300 hover:scale-105">
      {/* Poster Container */}
      <div 
        className="relative overflow-hidden rounded-t-lg aspect-[2/3] flex flex-col items-center justify-center text-white shadow-lg"
        style={gradientStyle}
      >
        {/* Subtle overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/20"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center p-4 flex flex-col items-center justify-center h-full">
          {/* Emoji */}
          <div className="text-6xl mb-4 drop-shadow-lg">
            {movie.emoji || '🎬'}
          </div>
          
          {/* Title */}
          <h3 className="font-heading font-bold text-lg text-center leading-tight drop-shadow-md">
            {movie.title || 'Untitled Movie'}
          </h3>
        </div>

        {/* Action Buttons */}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={handleEditClick}
            className="p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors cursor-pointer"
            aria-label="Edit movie"
            type="button"
          >
            <FiEdit2 className="w-4 h-4 pointer-events-none" />
          </button>
          <button
            onClick={handleDeleteClick}
            className="p-2 bg-black/50 rounded-full text-white hover:bg-red-500/70 transition-colors cursor-pointer"
            aria-label="Delete movie"
            type="button"
          >
            <FiTrash2 className="w-4 h-4 pointer-events-none" />
          </button>
        </div>

        {/* Decorative elements for visual appeal */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          {/* Top shine effect */}
          <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/20 to-transparent"></div>
          
          {/* Bottom shadow */}
          <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black/30 to-transparent"></div>
        </div>
      </div>

      {/* Content Footer */}
      <div className="p-3 bg-white rounded-b-lg">
        <p className="text-xs text-secondary-light font-medium text-center">
          Personal Movie
        </p>
      </div>
    </div>
  );
};

export default PersonalMovieCard;